import React, { useState } from "react";

import { CopyToClipboardButton } from "@@components";
import { AddCircle, Check, Close, Delete, Edit, Label, OpenInNew, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Link, List, ListItem, Stack, TextField, Typography } from "@mui/material";

import { formatTimestampSortable } from "../../../common/formatTimestamp";
import { ATTRIBUTE_PREFIX_CONFIG } from "./Consts";

const secretAttributeKeywords = ["secret", "password", "connectionstring"];

interface AttributesListProps {
	attributes?: { [key: string]: any } | null;
	title?: string;
	emptyMessage?: string;
	onSaveCustomAttributes?: (attributes: { [key: string]: string }) => Promise<void>;
	onDeleteAttributes?: (keys: string[]) => Promise<void>;
	secretKeys?: string[];
}

export default function AttributesList({
	attributes,
	title = "Attributes",
	emptyMessage = "No attributes defined",
	onSaveCustomAttributes,
	onDeleteAttributes,
	secretKeys = [],
}: AttributesListProps) {
	const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());
	const [isEditingCustom, setIsEditingCustom] = useState(false);
	const [editedCustomAttributes, setEditedCustomAttributes] = useState<{ [key: string]: string }>({});
	const [originalCustomAttributeKeys, setOriginalCustomAttributeKeys] = useState<Set<string>>(new Set());
	const [attributeIdMap, setAttributeIdMap] = useState<Map<string, string>>(new Map());
	const [isSaving, setIsSaving] = useState(false);
	const [isEditingAll, setIsEditingAll] = useState(false);
	const [attributesToDelete, setAttributesToDelete] = useState<Set<string>>(new Set());

	const handleStartEditingCustom = () => {
		// Initialize edited attributes with current custom attributes
		const customAttrs: { [key: string]: string } = {};
		const originalKeys = new Set<string>();
		const idMap = new Map<string, string>();
		if (attributes) {
			Object.entries(attributes).forEach(([key, value]) => {
				if (key.startsWith("Custom:")) {
					customAttrs[key] = String(value);
					originalKeys.add(key);
					idMap.set(key, key);
				}
			});
		}
		setEditedCustomAttributes(customAttrs);
		setOriginalCustomAttributeKeys(originalKeys);
		setAttributeIdMap(idMap);
		setIsEditingCustom(true);
	};

	const handleStartEditingAll = () => {
		setIsEditingAll(true);
		setAttributesToDelete(new Set());
	};

	const handleCancelEditingAll = () => {
		setIsEditingAll(false);
		setAttributesToDelete(new Set());
	};

	const handleSaveEditingAll = async () => {
		if (!onDeleteAttributes || attributesToDelete.size === 0) {
			setIsEditingAll(false);
			setAttributesToDelete(new Set());
			return;
		}

		setIsSaving(true);
		try {
			await onDeleteAttributes(Array.from(attributesToDelete));
			setIsEditingAll(false);
			setAttributesToDelete(new Set());
		} catch (error) {
			console.error("Error deleting attributes:", error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleToggleAttributeDelete = (key: string) => {
		setAttributesToDelete((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(key)) {
				newSet.delete(key);
			} else {
				newSet.add(key);
			}
			return newSet;
		});
	};

	const handleCancelEditingCustom = () => {
		setIsEditingCustom(false);
		setEditedCustomAttributes({});
		setOriginalCustomAttributeKeys(new Set());
		setAttributeIdMap(new Map());
	};

	const handleSaveCustom = async () => {
		if (!onSaveCustomAttributes) return;

		setIsSaving(true);
		try {
			await onSaveCustomAttributes(editedCustomAttributes);
			setIsEditingCustom(false);
			setEditedCustomAttributes({});
			setOriginalCustomAttributeKeys(new Set());
			setAttributeIdMap(new Map());
		} catch (error) {
			console.error("Error saving custom attributes:", error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleAddCustomAttribute = () => {
		const newKey = `Custom:NewAttribute${Object.keys(editedCustomAttributes).length + 1}`;
		const uniqueId = `new-${Date.now()}-${Math.random()}`;
		setEditedCustomAttributes((prev) => ({
			...prev,
			[newKey]: "",
		}));
		setAttributeIdMap((prev) => {
			const newMap = new Map(prev);
			newMap.set(newKey, uniqueId);
			return newMap;
		});
	};

	const handleCustomAttributeKeyChange = (oldKey: string, newKey: string) => {
		if (oldKey === newKey) return;

		// Ensure new key has Custom: prefix
		const prefixedKey = newKey.startsWith("Custom:") ? newKey : `Custom:${newKey}`;

		setEditedCustomAttributes((prev) => {
			const updated = { ...prev };
			updated[prefixedKey] = updated[oldKey];
			delete updated[oldKey];
			return updated;
		});
		setAttributeIdMap((prev) => {
			const newMap = new Map(prev);
			const id = newMap.get(oldKey);
			if (id) {
				newMap.delete(oldKey);
				newMap.set(prefixedKey, id);
			}
			return newMap;
		});
	};

	const handleCustomAttributeValueChange = (key: string, value: string) => {
		setEditedCustomAttributes((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleDeleteCustomAttribute = (key: string) => {
		setEditedCustomAttributes((prev) => {
			const updated = { ...prev };
			delete updated[key];
			return updated;
		});
		setAttributeIdMap((prev) => {
			const newMap = new Map(prev);
			newMap.delete(key);
			return newMap;
		});
	};

	const secretKeySet = new Set(secretKeys.map((key) => key.toLowerCase()));

	const isSecretAttribute = (key: string): boolean => {
		const lowerKey = key.toLowerCase();
		return secretKeySet.has(lowerKey) || secretAttributeKeywords.some((keyword) => lowerKey.includes(keyword));
	};

	const toggleSecretVisibility = (key: string) => {
		setVisibleSecrets((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(key)) {
				newSet.delete(key);
			} else {
				newSet.add(key);
			}
			return newSet;
		});
	};

	const renderAttributeValue = (key: string, value: string, fullKey = key) => {
		const isSecret = isSecretAttribute(key);
		const isFullKeySecret = isSecretAttribute(fullKey);
		const isVisible = visibleSecrets.has(key);
		const isHiddenSecret = (isSecret || isFullKeySecret) && !isVisible;
		const displayValue = isHiddenSecret ? "••••••••••••" : formatDisplayValue(value);

		if (value.startsWith("https://") && ((!isSecret && !isFullKeySecret) || isVisible)) {
			return (
				<Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
					<Link
						href={value}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							wordBreak: "break-all",
							flexShrink: 1,
							flexGrow: 1,
						}}
					>
						{value}
					</Link>
					{!isEditingAll && (
						<IconButton size="small" onClick={() => window.open(value, "_blank")} title="Open in new window" sx={{ p: 0.25 }}>
							<OpenInNew sx={{ fontSize: 14 }} />
						</IconButton>
					)}
				</Stack>
			);
		}
		return (
			<Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
				<Typography variant="body2" sx={{ wordBreak: "break-all", flexShrink: 1, flexGrow: 1 }}>
					{displayValue}
				</Typography>
				{(isSecret || isFullKeySecret) && (
					<IconButton
						size="small"
						onClick={() => toggleSecretVisibility(key)}
						title={isVisible ? "Hide value" : "Show value"}
						sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }}
					>
						{isVisible ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
					</IconButton>
				)}
				{!isEditingAll && <CopyToClipboardButton value={value} size="small" iconSize={14} edge={false} sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }} />}
			</Stack>
		);
	};

	if (!attributes || Object.keys(attributes).length === 0) {
		return (
			<>
				<Divider sx={{ my: 2 }} />
				<Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
					{emptyMessage}
				</Typography>
			</>
		);
	}

	return (
		<>
			<Divider sx={{ my: 2 }} />

			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
					{title} ({Object.keys(attributes).length})
				</Typography>
				{onDeleteAttributes && !isEditingAll && (
					<IconButton size="small" onClick={handleStartEditingAll} title="Edit attributes" sx={{ p: 0.5 }}>
						<Edit sx={{ fontSize: 16 }} />
					</IconButton>
				)}
				{onDeleteAttributes && isEditingAll && (
					<Stack direction="row" spacing={0.5}>
						<IconButton size="small" onClick={handleSaveEditingAll} disabled={isSaving} title="Save changes" color="primary" sx={{ p: 0.5 }}>
							<Check sx={{ fontSize: 16 }} />
						</IconButton>
						<IconButton size="small" onClick={handleCancelEditingAll} disabled={isSaving} title="Cancel" sx={{ p: 0.5 }}>
							<Close sx={{ fontSize: 16 }} />
						</IconButton>
					</Stack>
				)}
			</Stack>

			{(() => {
				// Group attributes by prefix
				const groupedAttributes: Record<string, Array<[string, any]>> = {};
				const ungroupedAttributes: Array<[string, any]> = [];

				Object.entries(attributes).forEach(([key, value]) => {
					let matchedPrefix = "";

					for (const prefix of Object.keys(ATTRIBUTE_PREFIX_CONFIG)) {
						if (key.startsWith(prefix)) {
							matchedPrefix = prefix;
							break;
						}
					}

					if (matchedPrefix) {
						if (!groupedAttributes[matchedPrefix]) {
							groupedAttributes[matchedPrefix] = [];
						}
						const displayKey = key.substring(matchedPrefix.length);
						groupedAttributes[matchedPrefix].push([displayKey, value]);
					} else {
						ungroupedAttributes.push([key, value]);
					}
				});

				// Separate Custom: prefix from other prefixes
				const customAttributes = groupedAttributes["Custom:"] || [];
				const otherPrefixes = Object.keys(groupedAttributes).filter((p) => p !== "Custom:");

				// Sort groups by prefix name and attributes within each group
				const sortedGroups = otherPrefixes.sort();
				sortedGroups.forEach((prefix) => {
					groupedAttributes[prefix].sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
				});
				customAttributes.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
				ungroupedAttributes.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

				return (
					<>
						{/* Render grouped attributes */}
						{sortedGroups.map((prefix) => {
							const prefixConfig = ATTRIBUTE_PREFIX_CONFIG[prefix];
							const IconComponent = prefixConfig.icon;
							const attributesInGroup = groupedAttributes[prefix];

							return (
								<div key={prefix}>
									{/* Section header with prefix and icon */}
									<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
										<IconComponent
											sx={{
												fontSize: 18,
												color: prefixConfig.color,
												flexShrink: 0,
											}}
											titleAccess={prefixConfig.tooltip}
										/>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold", color: prefixConfig.color }}>
											{prefix.replace(":", "")} ({attributesInGroup.length})
										</Typography>
									</Stack>

									{/* Attributes in this group */}
									<List dense sx={{ py: 0, pl: 2 }}>
										{attributesInGroup.map(([displayKey, value], index) => {
											const fullKey = `${prefix}${displayKey}`;
											const isMarkedForDeletion = attributesToDelete.has(fullKey);
											return (
												<ListItem
													key={index}
													sx={{
														px: 0,
														py: 1,
														alignItems: "flex-start",
														flexDirection: "column",
														opacity: isMarkedForDeletion ? 0.5 : 1,
														textDecoration: isMarkedForDeletion ? "line-through" : "none",
													}}
													secondaryAction={
														isEditingAll ? (
															<IconButton
																edge="end"
																size="small"
																onClick={() => handleToggleAttributeDelete(fullKey)}
																color={isMarkedForDeletion ? "primary" : "error"}
																title={isMarkedForDeletion ? "Undo delete" : "Delete attribute"}
																sx={{ mt: -0.5 }}
																disabled={isSaving}
															>
																<Delete fontSize="small" />
															</IconButton>
														) : null
													}
												>
													<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, alignSelf: "flex-start" }}>
														{displayKey}
													</Typography>
													{renderAttributeValue(displayKey, String(value), fullKey)}
												</ListItem>
											);
										})}
									</List>
								</div>
							);
						})}

						{/* Render ungrouped attributes */}
						{ungroupedAttributes.length > 0 && (
							<div>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
									<Label
										sx={{
											fontSize: 18,
											color: "text.secondary",
											flexShrink: 0,
										}}
										titleAccess="Other attributes"
									/>
									<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
										Other ({ungroupedAttributes.length})
									</Typography>
								</Stack>
								<List dense sx={{ py: 0, pl: sortedGroups.length > 0 ? 2 : 0 }}>
									{ungroupedAttributes.map(([key, value], index) => {
										const isMarkedForDeletion = attributesToDelete.has(key);
										return (
											<ListItem
												key={index}
												sx={{
													px: 0,
													py: 1,
													alignItems: "flex-start",
													flexDirection: "column",
													opacity: isMarkedForDeletion ? 0.5 : 1,
													textDecoration: isMarkedForDeletion ? "line-through" : "none",
												}}
												secondaryAction={
													isEditingAll ? (
														<IconButton
															edge="end"
															size="small"
															onClick={() => handleToggleAttributeDelete(key)}
															color={isMarkedForDeletion ? "primary" : "error"}
															title={isMarkedForDeletion ? "Undo delete" : "Delete attribute"}
															sx={{ mt: -0.5 }}
															disabled={isSaving}
														>
															<Delete fontSize="small" />
														</IconButton>
													) : null
												}
											>
												<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, alignSelf: "flex-start" }}>
													{key}
												</Typography>
												{renderAttributeValue(key, String(value))}
											</ListItem>
										);
									})}
								</List>
							</div>
						)}
						{(customAttributes.length > 0 || isEditingCustom || onSaveCustomAttributes) && (
							<div>
								{/* Section header with prefix and icon */}
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
									{(() => {
										const prefixConfig = ATTRIBUTE_PREFIX_CONFIG["Custom:"];
										const IconComponent = prefixConfig.icon;
										return (
											<>
												<IconComponent
													sx={{
														fontSize: 18,
														color: prefixConfig.color,
														flexShrink: 0,
													}}
													titleAccess={prefixConfig.tooltip}
												/>
												<Typography variant="subtitle2" sx={{ fontWeight: "bold", color: prefixConfig.color, flexGrow: 1 }}>
													Custom ({isEditingCustom ? Object.keys(editedCustomAttributes).length : customAttributes.length})
												</Typography>
											</>
										);
									})()}

									{/* Edit/Save/Cancel buttons */}
									{onSaveCustomAttributes && (
										<>
											{!isEditingCustom ? (
												<IconButton size="small" onClick={handleStartEditingCustom} title="Edit custom attributes" sx={{ p: 0.5 }}>
													<Edit sx={{ fontSize: 16 }} />
												</IconButton>
											) : (
												<Stack direction="row" spacing={0.5}>
													<IconButton size="small" onClick={handleSaveCustom} disabled={isSaving} title="Save changes" color="primary" sx={{ p: 0.5 }}>
														<Check sx={{ fontSize: 16 }} />
													</IconButton>
													<IconButton size="small" onClick={handleCancelEditingCustom} disabled={isSaving} title="Cancel" sx={{ p: 0.5 }}>
														<Close sx={{ fontSize: 16 }} />
													</IconButton>
												</Stack>
											)}
										</>
									)}
								</Stack>

								{/* Attributes in this group */}
								{!isEditingCustom ? (
									// Read-only view
									customAttributes.length > 0 ? (
										<List dense sx={{ py: 0, pl: 2 }}>
											{customAttributes.map(([displayKey, value], index) => (
												<ListItem key={index} sx={{ px: 0, py: 1, alignItems: "flex-start", flexDirection: "column" }}>
													<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, alignSelf: "flex-start" }}>
														{displayKey}
													</Typography>
													{renderAttributeValue(displayKey, String(value), `Custom:${displayKey}`)}
												</ListItem>
											))}
										</List>
									) : (
										<Box sx={{ pl: 2, py: 1 }}>
											<Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
												No custom attributes defined
											</Typography>
										</Box>
									)
								) : (
									// Edit mode
									<Box sx={{ pl: 2 }}>
										<Stack spacing={1.5}>
											{Object.entries(editedCustomAttributes).map(([fullKey, value]) => {
												const displayKey = fullKey.substring("Custom:".length);
												const isNewlyAdded = !originalCustomAttributeKeys.has(fullKey);
												const stableId = attributeIdMap.get(fullKey) || fullKey;
												return (
													<React.Fragment key={stableId}>
														<Stack direction="row" spacing={1}>
															<Stack direction="column" spacing={1} alignItems="flex-start" flex={1}>
																<TextField
																	size="small"
																	label="Key"
																	value={displayKey}
																	onChange={(e) => handleCustomAttributeKeyChange(fullKey, `Custom:${e.target.value}`)}
																	fullWidth
																	disabled={isSaving || !isNewlyAdded}
																	slotProps={{
																		input: {
																			readOnly: !isNewlyAdded,
																		},
																	}}
																/>
																<TextField
																	size="small"
																	label="Value"
																	value={value}
																	onChange={(e) => handleCustomAttributeValueChange(fullKey, e.target.value)}
																	fullWidth
																	disabled={isSaving}
																/>
															</Stack>
															<IconButton
																size="small"
																onClick={() => handleDeleteCustomAttribute(fullKey)}
																disabled={isSaving}
																title="Delete attribute"
																sx={{ mt: 0.5, visibility: isNewlyAdded ? "hidden" : "visible" }}
															>
																<Delete sx={{ fontSize: 18 }} />
															</IconButton>
														</Stack>

														<Divider sx={{ mt: 1, mb: 0 }} />
													</React.Fragment>
												);
											})}

											{/* Add new attribute button */}
											<Button size="small" startIcon={<AddCircle />} onClick={handleAddCustomAttribute} disabled={isSaving} sx={{ alignSelf: "flex-start", mt: 1 }}>
												Add Attribute
											</Button>
										</Stack>
									</Box>
								)}
							</div>
						)}
					</>
				);
			})()}
		</>
	);
}

function formatDisplayValue(value: string) {
	if (!isParsableTimestamp(value)) {
		return value;
	}

	return formatTimestampSortable(value);
}

function isParsableTimestamp(value: string) {
	const trimmedValue = value.trim();
	if (!looksLikeTimestamp(trimmedValue)) {
		return false;
	}

	const parsedDate = new Date(trimmedValue);
	return !Number.isNaN(parsedDate.getTime());
}

function looksLikeTimestamp(value: string) {
	return /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(value)
		|| /^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(value);
}
